/**
 * TypeScript mirror of the Cabinet v1 read contract.
 *
 * Source of truth: `CABINET_DESIGN.md` § "Graph API contract (PINNED)" —
 * `GET /api/v3/cabinet/graph` and `GET /api/v3/cabinet/summary`, served by
 * `backend/app/modules/cabinet/api/router.py`.
 *
 * Node ids are namespaced: `deal:<name>`, `doc:<document_uuid>`,
 * `entity:<entity_uuid>`. Timestamps are epoch-ms ints (backend JSON rule).
 *
 * Hand-rolled boundary validation follows the `evidenceLineage.ts` convention —
 * the repo does not use Zod inside services.
 */

export type CabinetNodeKind = "deal" | "document" | "entity";
export type CabinetObjectStatus = "current" | "superseded" | "duplicate";
export type CabinetEdgeType =
	| "belongs_to"
	| "member_of_family"
	| "references"
	/**
	 * Two logical documents in DIFFERENT deals whose canonical files are
	 * byte-identical. Derived by the backend at projection time from the
	 * hashes — it is an observation about two rooms, never a change to either,
	 * so neither document's status or version chain is affected.
	 */
	| "same_content"
	/**
	 * Two CANONICAL entities the resolution agent proposes belong under one
	 * umbrella. Inferred and REVERSIBLE — a link between rows, never a merge of
	 * them, so `evidence` carries the label and the rationale it was proposed on.
	 */
	| "entity_group"
	/**
	 * A misfiled document and the deal its entity actually lives in. Inferred
	 * from lint plus where the entity's other documents sit; a suggestion, not a
	 * move — nothing in the catalog has been changed.
	 */
	| "suggested_home";

/**
 * One physical file in a logical document's version chain. `supersedes` /
 * `duplicate_of` relations live here (between objects), not in `edges`.
 */
export interface CabinetVersion {
	relpath: string;
	sha256: string;
	status: CabinetObjectStatus;
	in_body_date?: string | null;
	evidence?: string | null;
}

export interface CabinetNodeMeta {
	/** Documents only. */
	period?: string | null;
	/** Documents only — ordered oldest → newest by the backend. */
	versions?: CabinetVersion[];
	/** Entities only. */
	entity_type?: string | null;
	/** Entities only — every room this canonical entity appears in. */
	deals?: string[];
	/** Entities only — every spelling observed for it in document bodies. */
	aliases?: string[];
	[key: string]: unknown;
}

export interface CabinetNode {
	id: string;
	kind: CabinetNodeKind;
	label: string;
	deal?: string | null;
	/** Documents only. */
	doc_type?: string | null;
	/** Documents only — status of the canonical object. */
	status?: CabinetObjectStatus | null;
	/** Inferred classifications only. */
	confidence?: number | null;
	evidence?: string | null;
	meta: CabinetNodeMeta;
}

export interface CabinetEdge {
	src: string;
	dst: string;
	type: CabinetEdgeType;
	deterministic: boolean;
	confidence?: number | null;
	/**
	 * The backend sends a JSON OBJECT here (`GraphEdge.evidence` is a dict) —
	 * e.g. a `same_content` edge carries `{basis, content_sha256, relpaths,
	 * deals}`. A string is still accepted because older payloads used one.
	 */
	evidence?: Record<string, unknown> | string | null;
}

export interface CabinetGraph {
	generated_at: number;
	nodes: CabinetNode[];
	edges: CabinetEdge[];
}

export interface CabinetSummary {
	deals: number;
	documents: number;
	objects: number;
	entities: number;
	edges_by_type: Record<string, number>;
	lint_findings: number;
	last_ingest_at: number | null;
}

export const CABINET_NODE_KINDS: readonly CabinetNodeKind[] = [
	"deal",
	"document",
	"entity",
];

export const CABINET_EDGE_TYPES: readonly CabinetEdgeType[] = [
	"belongs_to",
	"member_of_family",
	"references",
	"same_content",
	"entity_group",
	"suggested_home",
];

const OBJECT_STATUSES: readonly string[] = ["current", "superseded", "duplicate"];

// ---------------------------------------------------------------------------
// Boundary validation (dependency-free — matches evidenceLineage.ts style)
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | null {
	return typeof value === "string" ? value : null;
}

function optionalNumber(value: unknown): number | null {
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Edge evidence: an object from the backend, or a legacy plain string. */
function optionalEvidence(
	value: unknown,
): Record<string, unknown> | string | null {
	if (typeof value === "string") return value;
	return isRecord(value) ? value : null;
}

function parseVersions(value: unknown): CabinetVersion[] {
	if (!Array.isArray(value)) return [];
	const versions: CabinetVersion[] = [];
	for (const raw of value) {
		// A malformed entry is dropped rather than failing the whole graph — the
		// version chain is detail, the node itself is still worth rendering.
		if (!isRecord(raw) || typeof raw.relpath !== "string") continue;
		versions.push({
			relpath: raw.relpath,
			sha256: typeof raw.sha256 === "string" ? raw.sha256 : "",
			status:
				typeof raw.status === "string" && OBJECT_STATUSES.includes(raw.status)
					? (raw.status as CabinetObjectStatus)
					: "current",
			in_body_date: optionalString(raw.in_body_date),
			evidence: optionalString(raw.evidence),
		});
	}
	return versions;
}

function parseNode(raw: unknown): CabinetNode | null {
	if (!isRecord(raw)) return null;
	if (typeof raw.id !== "string" || raw.id.length === 0) return null;
	if (typeof raw.label !== "string") return null;
	if (
		typeof raw.kind !== "string" ||
		!CABINET_NODE_KINDS.includes(raw.kind as CabinetNodeKind)
	) {
		return null;
	}

	const meta = isRecord(raw.meta) ? raw.meta : {};
	return {
		id: raw.id,
		kind: raw.kind as CabinetNodeKind,
		label: raw.label,
		deal: optionalString(raw.deal),
		doc_type: optionalString(raw.doc_type),
		status:
			typeof raw.status === "string" && OBJECT_STATUSES.includes(raw.status)
				? (raw.status as CabinetObjectStatus)
				: null,
		confidence: optionalNumber(raw.confidence),
		evidence: optionalString(raw.evidence),
		meta: {
			...meta,
			period: optionalString(meta.period),
			entity_type: optionalString(meta.entity_type),
			versions: parseVersions(meta.versions),
		},
	};
}

function parseEdge(raw: unknown): CabinetEdge | null {
	if (!isRecord(raw)) return null;
	if (typeof raw.src !== "string" || typeof raw.dst !== "string") return null;
	if (
		typeof raw.type !== "string" ||
		!CABINET_EDGE_TYPES.includes(raw.type as CabinetEdgeType)
	) {
		return null;
	}
	return {
		src: raw.src,
		dst: raw.dst,
		type: raw.type as CabinetEdgeType,
		deterministic: raw.deterministic === true,
		confidence: optionalNumber(raw.confidence),
		evidence: optionalEvidence(raw.evidence),
	};
}

/**
 * Structurally validate a `/cabinet/graph` payload.
 *
 * Returns `null` when the envelope itself is wrong (not an object, missing
 * `nodes`/`edges` arrays, or a node that cannot be identified/typed). Individual
 * malformed EDGES are dropped instead — an edge referencing an unknown node kind
 * is not worth blanking the whole view for — as are edges whose endpoints are
 * not present in `nodes`, which ECharts would otherwise reject at render time.
 */
export function parseCabinetGraph(input: unknown): CabinetGraph | null {
	if (!isRecord(input)) return null;
	if (!Array.isArray(input.nodes) || !Array.isArray(input.edges)) return null;

	const nodes: CabinetNode[] = [];
	for (const raw of input.nodes) {
		const node = parseNode(raw);
		if (node === null) return null;
		nodes.push(node);
	}

	const ids = new Set(nodes.map((node) => node.id));
	const edges: CabinetEdge[] = [];
	for (const raw of input.edges) {
		const edge = parseEdge(raw);
		if (edge === null) continue;
		if (!ids.has(edge.src) || !ids.has(edge.dst)) continue;
		edges.push(edge);
	}

	return {
		generated_at: optionalNumber(input.generated_at) ?? 0,
		nodes,
		edges,
	};
}

/** Structurally validate a `/cabinet/summary` payload. */
export function parseCabinetSummary(input: unknown): CabinetSummary | null {
	if (!isRecord(input)) return null;
	const counts = ["deals", "documents", "objects", "entities", "lint_findings"];
	for (const key of counts) {
		if (optionalNumber(input[key]) === null) return null;
	}

	const edgesByType: Record<string, number> = {};
	if (isRecord(input.edges_by_type)) {
		for (const [key, value] of Object.entries(input.edges_by_type)) {
			const count = optionalNumber(value);
			if (count !== null) edgesByType[key] = count;
		}
	}

	return {
		deals: input.deals as number,
		documents: input.documents as number,
		objects: input.objects as number,
		entities: input.entities as number,
		edges_by_type: edgesByType,
		lint_findings: input.lint_findings as number,
		last_ingest_at: optionalNumber(input.last_ingest_at),
	};
}
